import { formatNumber, formatPercent } from "atlas_forecasting_app/lib/format"
import { clamp01, clampNumber } from "atlas_forecasting_app/lib/math"

function discreteQuestionMeta(question, probsLength = null) {
  const nominalMin = Number(question?.nominal_min)
  const rawNominalMax = Number(question?.nominal_max)
  const rawInboundCount = Number(question?.inbound_outcome_count)
  const inboundCount =
    Number.isInteger(rawInboundCount) && rawInboundCount >= 0
      ? rawInboundCount
      : Number.isInteger(probsLength) && probsLength >= 2
      ? probsLength - 2
      : null
  const nominalMax =
    Number.isFinite(rawNominalMax)
      ? rawNominalMax
      : Number.isFinite(nominalMin) && Number.isInteger(inboundCount) && inboundCount > 0
      ? nominalMin + inboundCount - 1
      : null

  return {
    nominalMin,
    nominalMax,
    inboundCount,
    openLowerBound: !!question?.open_lower_bound,
    openUpperBound: !!question?.open_upper_bound,
  }
}

function formatDiscreteOutcome(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return "—"
  return formatNumber(n, Number.isInteger(n) ? 0 : 2)
}

export function summarizeForecast(question, forecastData) {
  if (!question || forecastData == null) return "—"

  if (question.question_type === "binary") {
    const p = clamp01(Number(forecastData))
    if (p == null) return "—"
    return `P(Yes) ${(p * 100).toFixed(1)}%`
  }

  if (question.question_type === "multiple_choice") {
    if (!forecastData || typeof forecastData !== "object") return "—"
    const entries = Object.entries(forecastData)
      .map(([k, v]) => [k, clamp01(Number(v))])
      .filter(([, v]) => v != null)
      .sort((a, b) => b[1] - a[1])
    if (entries.length === 0) return "—"

    const [topLabel, topProbability] = entries[0]
    const semantics = multipleChoiceSemantics(question)

    if (semantics.kind === "marginal_yes") return `Highest P(Yes): ${topLabel} ${formatPercent(topProbability, 1)}`
    if (semantics.kind === "exclusive_incomplete") return `Most likely listed: ${topLabel} ${formatPercent(topProbability, 1)}`

    return `${topLabel} ${formatPercent(topProbability, 1)}`
  }

  if (question.question_type === "discrete") {
    const buckets = buildDiscreteBuckets(question, forecastData)
    const stats = statsFromDiscrete(buckets)
    if (!buckets || !stats) return "—"
    if (stats.mean != null) return `Mean ~ ${stats.mean.toFixed(2)}`
    if (stats.p50Label) return `Median ${stats.p50Label}`
    return `Distribution (${buckets.length})`
  }

  if (question.question_type === "numeric") {
    const distribution = extractNumericDistribution(forecastData)
    if (!distribution) return "—"

    const median = approximateQuantileFromNumericForecast(forecastData, 0.5, question)
    if (median == null) return `Distribution (${distribution.pmf.length})`
    return `Median ~ ${median.toFixed(2)}`
  }

  return "—"
}

export function multipleChoiceSemantics(question) {
  const mutuallyExclusive = question?.options_are_mutually_exclusive
  const comprehensive = question?.options_are_comprehensive

  if (mutuallyExclusive !== true && mutuallyExclusive !== false) {
    return {
      kind: "exclusive_comprehensive",
      topLabel: "Most likely",
      valueLabel: "Probability",
      note: "",
      canAggregateRemaining: true,
    }
  }

  if (!mutuallyExclusive) {
    return {
      kind: "marginal_yes",
      topLabel: "Highest P(Yes)",
      valueLabel: "P(Yes)",
      note: "Each option is forecast independently as a yes/no outcome. Multiple options can resolve yes, so the listed probabilities do not need to add up to 100%.",
      canAggregateRemaining: false,
    }
  }

  if (comprehensive !== true && comprehensive !== false) {
    return {
      kind: "exclusive_comprehensive",
      topLabel: "Most likely",
      valueLabel: "Probability",
      note: "",
      canAggregateRemaining: true,
    }
  }

  if (!comprehensive) {
    return {
      kind: "exclusive_incomplete",
      topLabel: "Most likely listed",
      valueLabel: "Probability",
      note: "At most one listed option can resolve yes, but unlisted outcomes are still possible, so the listed probabilities can add up to less than 100%.",
      canAggregateRemaining: true,
    }
  }

  return {
    kind: "exclusive_comprehensive",
    topLabel: "Most likely",
    valueLabel: "Probability",
    note: "",
    canAggregateRemaining: true,
  }
}

export function approximateQuantileFromCdf(cdf, q, question) {
  if (!Array.isArray(cdf) || cdf.length !== 201) return null

  const lower = Number(question.lower_bound)
  const upper = Number(question.upper_bound)
  const scale = question.scale_type || "linear"
  if (!Number.isFinite(lower) || !Number.isFinite(upper)) return null
  if (scale === "logarithmic" && (lower <= 0 || upper <= 0)) return null

  let idx = 0
  for (let i = 0; i < cdf.length; i += 1) {
    const p = clamp01(Number(cdf[i]))
    if (p == null) continue
    if (p >= q) {
      idx = i
      break
    }
    idx = i
  }

  if (scale === "logarithmic") {
    const increment = Math.pow(upper / lower, 1.0 / 200.0)
    if (idx === 0) return lower
    return lower * Math.pow(increment, idx)
  }

  const increment = (upper - lower) / 200.0
  if (idx === 0) return lower
  return lower + increment * idx
}

function normalizeNumericProbability(value) {
  const n = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(n)) return 0
  return clampNumber(n, 0, 1)
}

export function normalizeNumericProbabilityMassArray(arr) {
  if (!Array.isArray(arr) || arr.length !== 201) return null

  const probs = arr.map(normalizeNumericProbability)
  const sum = probs.reduce((acc, value) => acc + value, 0)
  if (sum > 1) return probs.map((value) => value / sum)
  return probs
}

export function normalizeNumericCdfArray(arr) {
  if (!Array.isArray(arr) || arr.length !== 201) return null

  const cdf = arr.map(normalizeNumericProbability)
  for (let i = 1; i < cdf.length; i += 1) {
    cdf[i] = Math.max(cdf[i], cdf[i - 1])
  }
  return cdf
}

export function numericProbabilityMassFromCdf(cdf) {
  if (!Array.isArray(cdf) || cdf.length !== 201) return null

  return cdf.map((value, idx) => {
    const prev = idx === 0 ? 0 : cdf[idx - 1]
    return clampNumber(value - prev, 0, 1)
  })
}

export function numericCdfFromProbabilityMass(probs) {
  if (!Array.isArray(probs) || probs.length !== 201) return null

  const cdf = []
  let total = 0
  for (const value of probs) {
    total = clamp01(total + value) ?? 0
    cdf.push(total)
  }
  return cdf
}

export function extractNumericDistribution(forecastData) {
  if (forecastData && typeof forecastData === "object" && !Array.isArray(forecastData)) {
    const pmf = normalizeNumericProbabilityMassArray(forecastData.probability_mass_function)
    const cdf = normalizeNumericCdfArray(forecastData.cumulative_distribution_function)

    if (pmf && cdf) return { pmf, cdf }
    if (pmf) return { pmf, cdf: numericCdfFromProbabilityMass(pmf) }
    if (cdf) return { pmf: numericProbabilityMassFromCdf(cdf), cdf }
    return null
  }

  const legacyCdf = normalizeNumericCdfArray(forecastData)
  if (!legacyCdf) return null

  return {
    pmf: numericProbabilityMassFromCdf(legacyCdf),
    cdf: legacyCdf,
  }
}

export function approximateQuantileFromNumericForecast(forecastData, q, question) {
  const distribution = extractNumericDistribution(forecastData)
  if (!distribution?.cdf) return null
  return approximateQuantileFromCdf(distribution.cdf, q, question)
}

export function normalizeProbabilityArray(arr) {
  if (!Array.isArray(arr)) return null
  const probs = arr
    .map((v) => (typeof v === "number" ? v : Number(v)))
    .map((v) => (Number.isFinite(v) ? clampNumber(v, 0, 1) : 0))
  const sum = probs.reduce((acc, n) => acc + n, 0)
  if (sum > 0) return probs.map((p) => p / sum)
  if (probs.length === 0) return []
  return probs.map(() => 1 / probs.length)
}

export function normalizeDiscreteProbabilityArray(arr, question) {
  const probs = normalizeProbabilityArray(arr)
  if (!probs) return null

  const meta = discreteQuestionMeta(question, probs.length)
  if (!Number.isInteger(meta.inboundCount) || meta.inboundCount < 0) return probs

  const expectedLength = meta.inboundCount + 2
  let normalized = probs

  if (normalized.length === meta.inboundCount) {
    normalized = [0, ...normalized, 0]
  } else if (normalized.length > expectedLength) {
    normalized = normalized.slice(0, expectedLength)
  } else if (normalized.length < expectedLength) {
    normalized = normalized.concat(Array(expectedLength - normalized.length).fill(0))
  }

  if (!meta.openLowerBound && normalized.length > 0) normalized[0] = 0
  if (!meta.openUpperBound && normalized.length > 1) normalized[normalized.length - 1] = 0

  const sum = normalized.reduce((acc, value) => acc + value, 0)
  if (sum > 0) return normalized.map((value) => value / sum)

  const fallback = normalized.map(() => 1)
  if (!meta.openLowerBound && fallback.length > 0) fallback[0] = 0
  if (!meta.openUpperBound && fallback.length > 1) fallback[fallback.length - 1] = 0

  const fallbackSum = fallback.reduce((acc, value) => acc + value, 0)
  return fallbackSum > 0 ? fallback.map((value) => value / fallbackSum) : normalized
}

export function discreteLabelForValue(question, value) {
  const meta = discreteQuestionMeta(question)

  if (meta.openLowerBound && Number.isFinite(meta.nominalMin) && Math.abs(value - (meta.nominalMin - 1)) < 1e-9) {
    return `<${formatDiscreteOutcome(meta.nominalMin)}`
  }

  if (meta.openUpperBound && Number.isFinite(meta.nominalMax) && Math.abs(value - (meta.nominalMax + 1)) < 1e-9) {
    return `>${formatDiscreteOutcome(meta.nominalMax)}`
  }

  return formatDiscreteOutcome(value)
}

export function buildDiscreteBuckets(question, forecastData) {
  const probs = normalizeDiscreteProbabilityArray(forecastData, question)
  if (!probs) return null

  const meta = discreteQuestionMeta(question, probs.length)
  const buckets = []
  const inRangeCount = Math.max(0, probs.length - 2)

  if (meta.openLowerBound && probs.length > 0) {
    buckets.push({
      kind: "below",
      label: Number.isFinite(meta.nominalMin) ? `<${formatDiscreteOutcome(meta.nominalMin)}` : "<min",
      probability: probs[0],
      plotValue: Number.isFinite(meta.nominalMin) ? meta.nominalMin - 1 : -1,
      exactValue: null,
    })
  }

  for (let i = 0; i < inRangeCount; i += 1) {
    const value = Number.isFinite(meta.nominalMin) ? meta.nominalMin + i : i
    buckets.push({
      kind: "exact",
      label: discreteLabelForValue(question, value),
      probability: probs[i + 1],
      plotValue: value,
      exactValue: value,
    })
  }

  if (meta.openUpperBound && probs.length > 1) {
    buckets.push({
      kind: "above",
      label: Number.isFinite(meta.nominalMax) ? `>${formatDiscreteOutcome(meta.nominalMax)}` : ">max",
      probability: probs[probs.length - 1],
      plotValue: Number.isFinite(meta.nominalMax) ? meta.nominalMax + 1 : inRangeCount,
      exactValue: null,
    })
  }

  return buckets.map((bucket, displayIndex) => ({ ...bucket, displayIndex }))
}

export function quantileFromDiscrete(buckets, q) {
  if (!Array.isArray(buckets) || buckets.length === 0) return null

  let cum = 0
  for (const bucket of buckets) {
    cum += clamp01(Number(bucket?.probability)) ?? 0
    if (cum >= q) return bucket
  }

  return buckets[buckets.length - 1]
}

export function statsFromDiscrete(buckets) {
  if (!Array.isArray(buckets) || buckets.length === 0) return null

  let mean = 0
  let total = 0
  let maxP = -1
  let hasTailMass = false
  let modeBucket = null

  for (const bucket of buckets) {
    const probability = clamp01(Number(bucket?.probability)) ?? 0
    total += probability

    if (bucket.kind === "exact" && Number.isFinite(bucket.exactValue)) {
      mean += bucket.exactValue * probability
    } else if (probability > 1e-12) {
      hasTailMass = true
    }

    if (probability > maxP) {
      maxP = probability
      modeBucket = bucket
    }
  }

  const p10Bucket = quantileFromDiscrete(buckets, 0.1)
  const p50Bucket = quantileFromDiscrete(buckets, 0.5)
  const p90Bucket = quantileFromDiscrete(buckets, 0.9)

  return {
    mean: !hasTailMass && total > 0 ? mean / total : null,
    mode: modeBucket?.plotValue ?? null,
    modeLabel: modeBucket?.label ?? null,
    modeIndex: modeBucket?.displayIndex ?? null,
    p10: p10Bucket?.plotValue ?? null,
    p10Label: p10Bucket?.label ?? null,
    p10Index: p10Bucket?.displayIndex ?? null,
    p50: p50Bucket?.plotValue ?? null,
    p50Label: p50Bucket?.label ?? null,
    p50Index: p50Bucket?.displayIndex ?? null,
    p90: p90Bucket?.plotValue ?? null,
    p90Label: p90Bucket?.label ?? null,
    p90Index: p90Bucket?.displayIndex ?? null,
    maxP,
  }
}
